import React from 'react'
import {
  isMysteryComboBetType,
  isMysterySelection,
  isNoveltyBetType,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { MysteryDetails, RaceDetails } from '@mobi/betslip/types'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings/hexColors'
import { setQualtricsBetData } from '@core/Areas/Qualtrics/qualtrics'
import type { QuickbetState } from '@core/Areas/Quickbet/driver'
import { SingleReceiptData } from '@core/Areas/Quickbet/types'
import { NoticeBox } from '@core/Components/NoticeBox'
import { Money } from '@core/Components/Text/Money'
import {
  ReceiptButtonsContainerStyled,
  ReceiptCampaignTextStyled,
  ReceiptContainerStyled,
  ReceiptListItemStyled,
  ReceiptListItemTextStyled,
  ReceiptListRowStyled,
  ReceiptListStyled,
} from '../Common/Receipt.styles'
import { RaceAlertButton, ShareButton } from './Components'

export const QuickbetReceipt: React.FC<
  {
    receiptData: SingleReceiptData
  } & Pick<QuickbetState, 'selection' | 'selectionDetails'>
> = ({ selection, selectionDetails, receiptData }) => {
  const isToteBet = isToteSelection(selection)
  const isMysteryBet = isMysterySelection(selection)
  const isMysteryCombo = isMysteryBet && isMysteryComboBetType(selection)
  const isNoveltyBet = isToteBet && isNoveltyBetType(selection.betType)

  setQualtricsData({
    isMysteryBet,
    isMysteryCombo,
    isNoveltyBet,
    isToteBet,
    selectionDetails,
    receiptData,
  })

  const shouldAllowShare = !isMysteryBet || !isMysteryCombo
  const specialOffers = receiptData.specialOffers || []

  const derivedBetCost: number =
    isToteBet || isMysteryBet
      ? (receiptData.betCost ?? 0)
      : (receiptData.winInvestment ?? 0) + (receiptData.placeInvestment ?? 0)
  const shouldDisplayFlexi = (isMysteryBet && !isMysteryCombo) || (isToteBet && isNoveltyBet)
  const projectedPay =
    (receiptData.projectedWinPayout ?? 0) + (receiptData.projectedPlacePayout ?? 0)
  const betCostMessage = 'Bet Cost'
  const wasCampaignActivated = receiptData.campaignActivatedInd && !!receiptData.bonusBet
  const isBonusBet = receiptData.campaignType == 'BonusBetReward'

  return (
    <ReceiptContainerStyled>
      <NoticeBox noticeBoxType='Success' title='Your bet has been placed.' paddingOverride='0'>
        <ReceiptListStyled>
          {shouldDisplayFlexi && (
            <ReceiptListRowStyled data-tid-receipt-flexi=''>
              <ReceiptListItemStyled>Flexi</ReceiptListItemStyled>
              <ReceiptListItemTextStyled>{receiptData.divXAnnotations}</ReceiptListItemTextStyled>
            </ReceiptListRowStyled>
          )}

          {!(isToteBet || isMysteryBet) && projectedPay > 0 && (
            <ReceiptListRowStyled data-tid-receipt-projrtn=''>
              <ReceiptListItemStyled>Projected Return</ReceiptListItemStyled>
              <ReceiptListItemTextStyled>
                <Money amount={projectedPay} />
              </ReceiptListItemTextStyled>
            </ReceiptListRowStyled>
          )}

          <ReceiptListRowStyled data-tid-receipt-betcost=''>
            <ReceiptListItemStyled>{betCostMessage}</ReceiptListItemStyled>
            <ReceiptListItemTextStyled>
              <Money amount={derivedBetCost} />
            </ReceiptListItemTextStyled>
          </ReceiptListRowStyled>

          {!!wasCampaignActivated && (
            <ReceiptListRowStyled data-tid-receipt-bonusbet=''>
              <ReceiptListItemStyled>
                <ReceiptCampaignTextStyled isBonusBet={isBonusBet}>
                  {isBonusBet ? 'Bonus Bet' : 'Bonus Cash'}
                </ReceiptCampaignTextStyled>
              </ReceiptListItemStyled>
              <ReceiptListItemTextStyled>
                <ReceiptCampaignTextStyled isBonusBet={isBonusBet}>
                  <Money amount={-(receiptData.bonusBet ?? 0)} />
                </ReceiptCampaignTextStyled>
              </ReceiptListItemTextStyled>
            </ReceiptListRowStyled>
          )}

          {!!wasCampaignActivated && (
            <ReceiptListRowStyled data-tid-receipt-amountpaid=''>
              <ReceiptListItemStyled>Amount Paid</ReceiptListItemStyled>
              <ReceiptListItemTextStyled>
                <Money amount={receiptData.amountPaid ?? 0} />
              </ReceiptListItemTextStyled>
            </ReceiptListRowStyled>
          )}

          <ReceiptListRowStyled data-tid-receipt-ticket=''>
            <ReceiptListItemStyled>Ticket</ReceiptListItemStyled>
            <ReceiptListItemTextStyled>{receiptData.ticketNumber}</ReceiptListItemTextStyled>
          </ReceiptListRowStyled>

          {specialOffers
            ? specialOffers.map((special, i) => {
                return (
                  <ReceiptListRowStyled key={i} data-tid-receipt-special=''>
                    <ReceiptListItemStyled>{special.title}</ReceiptListItemStyled>
                    <ReceiptListItemTextStyled>
                      <Icon type='specials' size='1.6rem' color={hexColors.orange} />
                    </ReceiptListItemTextStyled>
                  </ReceiptListRowStyled>
                )
              })
            : null}
        </ReceiptListStyled>
      </NoticeBox>

      <ReceiptButtonsContainerStyled>
        {shouldAllowShare && (
          <ShareButton
            winInvestment={receiptData.winInvestment}
            placeInvestment={receiptData.placeInvestment}
            bonusBet={receiptData.bonusBet}
            ticketNumber={receiptData.ticketNumber}
            betCost={receiptData.betCost}
            specialOffers={specialOffers}
            hasSpecialOffer={receiptData.hasSpecialOffer ?? false}
          />
        )}

        <RaceAlertButton selectionDetails={selectionDetails} />
      </ReceiptButtonsContainerStyled>
    </ReceiptContainerStyled>
  )
}

// =============
// Local Helpers
// =============

function setQualtricsData({
  selectionDetails,
  receiptData,
  isMysteryBet,
  isMysteryCombo,
  isNoveltyBet,
  isToteBet,
}: Pick<QuickbetState, 'selectionDetails'> & {
  isMysteryBet: boolean
  isMysteryCombo: boolean
  isNoveltyBet: boolean
  isToteBet: boolean
  receiptData: SingleReceiptData
}) {
  const specialOffers = receiptData.specialOffers

  let code = ''

  if (selectionDetails && 'races' in selectionDetails)
    code = (selectionDetails as RaceDetails).races[0].meetingCode
  if (selectionDetails && 'race' in selectionDetails)
    code = (selectionDetails as MysteryDetails).race.meetingCode

  if ((specialOffers && specialOffers.length > 0) || !!receiptData.bonusBet)
    setQualtricsBetData(
      'racing',
      receiptData.bonusBet ? 'bonus' : 'special',
      receiptData.bonusBet ? receiptData.bonusBet : (receiptData.amountPaid ?? 0),
      isNoveltyBet
        ? 'novelty'
        : isMysteryCombo
          ? 'mysteryCombo'
          : isMysteryBet
            ? 'mysteryBet'
            : isToteBet
              ? 'tote'
              : 'fob',
      code
    )
}
